<template>
    <div class="holder-analytics-charts">
        <!-- Holder Growth Chart — only show when data exists -->
        <div v-if="holderTrends.length > 0" class="chart-section compass-card">
            <div class="section-header">
                <h6 class="section-title">Holder Growth</h6>
                <span class="section-meta mono-num">
                    {{ formatNumber(holderTrends[holderTrends.length - 1]?.holder_count) }} holders
                </span>
            </div>
            <div class="chart-container">
                <canvas ref="holderGrowthCanvas"></canvas>
            </div>
        </div>

        <!-- Concentration Trend Chart — only show when data exists -->
        <div v-if="holderTrends.length > 0" class="chart-section compass-card">
            <div class="section-header">
                <h6 class="section-title">Concentration Trend</h6>
                <div class="legend-inline">
                    <span class="legend-item"><span class="legend-dot" :style="{background: concentrationColors[0]}"></span>Top 10</span>
                    <span class="legend-item"><span class="legend-dot" :style="{background: concentrationColors[1]}"></span>Top 50</span>
                    <span class="legend-item"><span class="legend-dot" :style="{background: concentrationColors[2]}"></span>Top 100</span>
                </div>
            </div>
            <div class="chart-container">
                <canvas ref="concentrationCanvas"></canvas>
            </div>
        </div>

        <!-- Balance Distribution: Treemap (value) + Horizontal bars (accounts) -->
        <div class="chart-section compass-card">
            <div class="section-header">
                <h6 class="section-title">Balance Distribution</h6>
                <span v-if="distribution" class="section-meta mono-num">
                    {{ formatNumber(distribution.total_holders) }} holders
                </span>
            </div>
            <div v-if="loadingDistribution" class="chart-loading">
                <div class="spinner-border spinner-border-sm text-primary"></div>
            </div>
            <div v-else-if="!distribution || distribution.buckets.length === 0" class="chart-empty">
                No distribution data available
            </div>
            <template v-else>
                <!-- Treemap: area proportional to USD value held -->
                <div class="chart-sublabel">Distribution by value held</div>
                <div class="chart-container treemap-container">
                    <canvas ref="treemapCanvas"></canvas>
                </div>
                <!-- Horizontal bar breakdown: accounts per range -->
                <div class="chart-sublabel" style="margin-top: 1.25rem;">Accounts per balance range</div>
                <div class="distribution-table">
                    <div v-for="bucket in distribution.buckets" :key="bucket.label" class="dist-row">
                        <span class="dist-label">{{ bucket.label }}</span>
                        <span class="dist-bar-wrap">
                            <span class="dist-bar" :style="{ width: Math.max(bucket.percent_holders, 0.5) + '%' }"></span>
                        </span>
                        <span class="dist-count mono-num">{{ formatNumber(bucket.count) }}</span>
                        <span class="dist-pct mono-num">{{ bucket.percent_holders }}%</span>
                    </div>
                </div>
            </template>
        </div>

        <!-- Hold Time Distribution -->
        <div class="chart-section compass-card">
            <div class="section-header">
                <h6 class="section-title">Hold Time Distribution</h6>
                <div v-if="holdTimeData" class="hold-time-stats">
                    <span class="stat-pill">Median: <strong class="mono-num">{{ formatDuration(holdTimeData.percentiles.p50) }}</strong></span>
                    <span class="stat-pill">Avg: <strong class="mono-num">{{ formatDuration(holdTimeData.stats.mean_seconds) }}</strong></span>
                    <span v-if="holdTimeData.bot_count > 0" class="stat-pill stat-pill-muted">
                        {{ formatNumber(holdTimeData.bot_count) }} bot/arb excluded (&lt;5s)
                    </span>
                </div>
            </div>
            <div v-if="loadingHoldTimes" class="chart-loading">
                <div class="spinner-border spinner-border-sm text-primary"></div>
            </div>
            <div v-else-if="!holdTimeData || holdTimeData.histogram.length === 0" class="chart-empty">
                No hold time data available
            </div>
            <template v-else>
                <div class="chart-container">
                    <canvas ref="holdTimeCanvas"></canvas>
                </div>
                <div class="percentile-grid">
                    <div class="percentile-item" v-for="(label, key) in percentileLabels" :key="key">
                        <span class="perc-label">{{ label }}</span>
                        <span class="perc-value mono-num">{{ formatDuration(holdTimeData.percentiles[key]) }}</span>
                    </div>
                </div>
            </template>
        </div>

        <!-- Large Swaps Table -->
        <div class="chart-section compass-card">
            <div class="section-header">
                <h6 class="section-title">Large Swaps</h6>
                <div class="whale-controls">
                    <div class="range-selector">
                        <div
                            v-for="r in ranges"
                            :key="r.value"
                            class="range-btn"
                            :class="{ active: selectedRange === r.value }"
                            @click="selectRange(r.value)"
                        >
                            {{ r.label }}
                        </div>
                    </div>
                    <select v-model="whaleMinUsd" @change="loadWhaleMovements" class="form-select form-select-sm whale-select">
                        <option :value="10000">$10K+</option>
                        <option :value="50000">$50K+</option>
                        <option :value="100000">$100K+</option>
                        <option :value="500000">$500K+</option>
                        <option :value="1000000">$1M+</option>
                    </select>
                </div>
            </div>
            <div v-if="loadingWhales" class="chart-loading">
                <div class="spinner-border spinner-border-sm text-primary"></div>
            </div>
            <div v-else-if="whaleMovements.length === 0" class="chart-empty">
                No large swaps above threshold in this period
            </div>
            <div v-else class="table-responsive">
                <table class="table table-sm compass-table whale-table mb-0">
                    <thead>
                        <tr>
                            <th>Time</th>
                            <th>Wallet</th>
                            <th>Side</th>
                            <th>Pair</th>
                            <th>DEX</th>
                            <th class="text-end">Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(m, idx) in whaleMovements" :key="idx">
                            <td class="mono-num td-time">{{ formatTime(m.timestamp) }}</td>
                            <td>
                                <div class="whale-wallet">
                                    <a :href="`https://solscan.io/account/${m.signer}`" target="_blank" class="wallet-addr">
                                        {{ m.name || truncAddr(m.signer) }}
                                    </a>
                                    <div v-if="m.labels && m.labels.length" class="whale-labels">
                                        <span v-for="l in m.labels.slice(0, 2)" :key="l" class="label-badge"
                                              :class="`label-${l.toLowerCase().replace(/[^a-z0-9]/g, '-')}`">{{ l }}</span>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <span class="direction-badge" :class="m.direction === 'buy' ? 'dir-buy' : 'dir-sell'">
                                    {{ m.direction === 'buy' ? 'Buy' : 'Sell' }}
                                </span>
                            </td>
                            <td class="pair-cell">
                                <span class="mono-num">{{ m.token_a?.symbol || truncAddr(m.token_a?.mint) }}</span>
                                <span class="pair-arrow">&rarr;</span>
                                <span class="mono-num">{{ m.token_b?.symbol || truncAddr(m.token_b?.mint) }}</span>
                            </td>
                            <td>
                                <span class="exchange-badge">{{ m.exchange }}</span>
                            </td>
                            <td class="text-end mono-num">${{ formatUsd(m.value_usd) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js'
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap'

Chart.register(...registerables, TreemapController, TreemapElement)

export default {
    name: 'HolderAnalyticsCharts',

    props: {
        mint: { type: String, required: true }
    },

    data() {
        return {
            selectedRange: '30d',
            ranges: [
                { value: '7d', label: '7D' },
                { value: '30d', label: '30D' },
                { value: '90d', label: '90D' },
            ],
            rangeLabels: { '7d': 'Past 7 days', '30d': 'Past 30 days', '90d': 'Past 90 days' },
            // Data
            holderTrends: [],
            distribution: null,
            holdTimeData: null,
            whaleMovements: [],
            whaleMinUsd: 10000,
            // Loading states
            loadingTrends: true,
            loadingDistribution: true,
            loadingHoldTimes: true,
            loadingWhales: true,
            // Chart instances
            holderGrowthChart: null,
            concentrationChart: null,
            treemapChart: null,
            distributionChart: null,
            holdTimeChart: null,
            // Percentile labels
            percentileLabels: {
                p10: 'P10',
                p25: 'P25',
                p50: 'Median',
                p75: 'P75',
                p90: 'P90',
                p95: 'P95',
            },
        }
    },

    mounted() {
        this.loadAll()
    },

    computed: {
        concentrationColors() {
            const dc = window.divergentColor || ['#9945ff', '#608aff', '#5cb7ff', '#9adaff', '#e9f6fb', '#b2f7ff', '#10f7cf']
            return [dc[0], dc[1], dc[6]]
        },
        chartTextColor() {
            return document.body.classList.contains('dark-mode') ? '#9ca3af' : '#6b7280'
        },
        chartGridColor() {
            return document.body.classList.contains('dark-mode')
                ? 'rgba(139, 92, 246, 0.08)'
                : 'rgba(139, 92, 246, 0.12)'
        }
    },

    watch: {
        mint() {
            this.loadAll()
        }
    },

    beforeUnmount() {
        this.destroyCharts()
    },

    methods: {
        loadAll() {
            this.loadHolderTrends()
            this.loadDistribution()
            this.loadHoldTimes()
            this.loadWhaleMovements()
        },

        selectRange(range) {
            this.selectedRange = range
            this.loadWhaleMovements()
        },

        destroyCharts() {
            if (this.holderGrowthChart) { this.holderGrowthChart.destroy(); this.holderGrowthChart = null }
            if (this.concentrationChart) { this.concentrationChart.destroy(); this.concentrationChart = null }
            if (this.treemapChart) { this.treemapChart.destroy(); this.treemapChart = null }
            if (this.distributionChart) { this.distributionChart.destroy(); this.distributionChart = null }
            if (this.holdTimeChart) { this.holdTimeChart.destroy(); this.holdTimeChart = null }
        },

        // === Data Fetching ===

        async loadHolderTrends() {
            this.loadingTrends = true
            try {
                const res = await fetch(`/api/v1/tokens/${this.mint}/holder-trends?range=${this.selectedRange}`)
                const json = await res.json()
                this.holderTrends = json.data?.snapshots || []
                this.$nextTick(() => {
                    this.renderHolderGrowthChart()
                    this.renderConcentrationChart()
                })
            } catch (e) {
                console.error('Error loading holder trends:', e)
                this.holderTrends = []
            } finally {
                this.loadingTrends = false
            }
        },

        async loadDistribution() {
            this.loadingDistribution = true
            try {
                const res = await fetch(`/api/v1/tokens/${this.mint}/holder-distribution`)
                const json = await res.json()
                this.distribution = json.data || null
                this.$nextTick(() => {
                    this.renderTreemapChart()
                })
            } catch (e) {
                console.error('Error loading distribution:', e)
                this.distribution = null
            } finally {
                this.loadingDistribution = false
            }
        },

        async loadHoldTimes() {
            this.loadingHoldTimes = true
            try {
                const res = await fetch(`/api/v1/tokens/${this.mint}/hold-times?min_seconds=5`)
                const json = await res.json()
                this.holdTimeData = json.data || null
                this.$nextTick(() => this.renderHoldTimeChart())
            } catch (e) {
                console.error('Error loading hold times:', e)
                this.holdTimeData = null
            } finally {
                this.loadingHoldTimes = false
            }
        },

        async loadWhaleMovements() {
            this.loadingWhales = true
            try {
                const res = await fetch(`/api/v1/tokens/${this.mint}/whale-movements?min_usd=${this.whaleMinUsd}&range=${this.selectedRange}&limit=30`)
                const json = await res.json()
                this.whaleMovements = json.data?.movements || []
            } catch (e) {
                console.error('Error loading whale movements:', e)
                this.whaleMovements = []
            } finally {
                this.loadingWhales = false
            }
        },

        // === Chart Rendering ===

        renderHolderGrowthChart() {
            if (this.holderGrowthChart) this.holderGrowthChart.destroy()
            if (!this.$refs.holderGrowthCanvas || !this.holderTrends.length) return

            const labels = this.holderTrends.map(s => this.formatDateShort(s.timestamp))
            const data = this.holderTrends.map(s => s.holder_count)

            this.holderGrowthChart = new Chart(this.$refs.holderGrowthCanvas, {
                type: 'line',
                data: {
                    labels,
                    datasets: [{
                        label: 'Holders',
                        data,
                        borderColor: '#8b5cf6',
                        backgroundColor: 'rgba(139, 92, 246, 0.1)',
                        borderWidth: 2,
                        fill: true,
                        tension: 0.3,
                        pointRadius: 0,
                        pointHitRadius: 8,
                    }]
                },
                options: this.getLineChartOptions({
                    tooltipPrefix: '',
                    tooltipSuffix: ' holders',
                    yFormat: (v) => this.formatNumber(v),
                })
            })
        },

        renderConcentrationChart() {
            if (this.concentrationChart) this.concentrationChart.destroy()
            if (!this.$refs.concentrationCanvas || !this.holderTrends.length) return

            const labels = this.holderTrends.map(s => this.formatDateShort(s.timestamp))
            const cc = this.concentrationColors

            this.concentrationChart = new Chart(this.$refs.concentrationCanvas, {
                type: 'line',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'Top 10',
                            data: this.holderTrends.map(s => s.top_10_percent),
                            borderColor: cc[0],
                            backgroundColor: cc[0] + '0d',
                            borderWidth: 2, fill: false, tension: 0.3, pointRadius: 0, pointHitRadius: 8,
                        },
                        {
                            label: 'Top 50',
                            data: this.holderTrends.map(s => s.top_50_percent),
                            borderColor: cc[1],
                            backgroundColor: cc[1] + '0d',
                            borderWidth: 2, fill: false, tension: 0.3, pointRadius: 0, pointHitRadius: 8,
                        },
                        {
                            label: 'Top 100',
                            data: this.holderTrends.map(s => s.top_100_percent),
                            borderColor: cc[2],
                            backgroundColor: cc[2] + '0d',
                            borderWidth: 2, fill: false, tension: 0.3, pointRadius: 0, pointHitRadius: 8,
                        },
                    ]
                },
                options: this.getLineChartOptions({
                    tooltipSuffix: '%',
                    yFormat: (v) => v.toFixed(1) + '%',
                    yMax: 100,
                })
            })
        },

        renderTreemapChart() {
            if (this.treemapChart) this.treemapChart.destroy()
            if (!this.$refs.treemapCanvas || !this.distribution?.buckets?.length) return

            const buckets = this.distribution.buckets.filter(b => b.balance_usd > 0)
            // Use the warm palette (palleteColor) — 7 colors matching 7 balance buckets
            const colors = window.palleteColor || ['#a527b3', '#d5069c', '#f50b80', '#ff3763', '#ff5e47', '#ff8329', '#ffa600']
            const totalUsd = this.distribution.total_balance_usd
            const totalAccounts = buckets.reduce((s, b) => s + b.count, 0)

            // Assign boldest colors to largest buckets (reverse index so highest-value gets first color)
            const n = buckets.length
            const bgColors = buckets.map((_, i) => colors[(n - 1 - i) % colors.length] + 'cc')
            const bdColors = buckets.map((_, i) => colors[(n - 1 - i) % colors.length])
            const self = this

            this.treemapChart = new Chart(this.$refs.treemapCanvas, {
                type: 'treemap',
                data: {
                    datasets: [{
                        tree: buckets.map((b, i) => ({
                            label: b.label,
                            value: b.balance_usd,
                            count: b.count,
                            pctValue: totalUsd > 0 ? (b.balance_usd / totalUsd * 100).toFixed(1) : '0',
                            pctAccounts: totalAccounts > 0 ? (b.count / totalAccounts * 100).toFixed(1) : '0',
                            _colorIdx: i,
                        })),
                        key: 'value',
                        groups: ['label'],
                        backgroundColor(ctx) {
                            if (!ctx.raw || ctx.dataIndex == null) return bgColors[0]
                            const d = ctx.raw._data
                            const idx = d?.children?.[0]?._colorIdx ?? d?._colorIdx ?? ctx.dataIndex
                            return bgColors[idx % bgColors.length]
                        },
                        borderColor(ctx) {
                            if (!ctx.raw || ctx.dataIndex == null) return bdColors[0]
                            const d = ctx.raw._data
                            const idx = d?.children?.[0]?._colorIdx ?? d?._colorIdx ?? ctx.dataIndex
                            return bdColors[idx % bdColors.length]
                        },
                        borderWidth: 1,
                        spacing: 2,
                        labels: {
                            display: true,
                            align: 'center',
                            position: 'middle',
                            color: '#ffffff',
                            font: { size: 11, weight: 'bold' },
                            formatter(ctx) {
                                const d = ctx.raw?._data
                                if (!d) return ctx.raw?.g || ''
                                const label = d.label || d.children?.[0]?.label || ctx.raw?.g || ''
                                const value = d.value || d.children?.[0]?.value || 0
                                return [label, '$' + self.formatUsd(value)]
                            },
                        },
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                title: (items) => {
                                    if (!items.length) return ''
                                    const d = items[0].raw?._data
                                    return d?.label || items[0].raw?.g || ''
                                },
                                label: (ctx) => {
                                    const d = ctx.raw?._data
                                    if (!d) return ''
                                    const child = d.children?.[0] || d
                                    const val = d.value || child.value || 0
                                    const count = child.count || 0
                                    const pv = totalUsd > 0 ? (val / totalUsd * 100).toFixed(1) : '0'
                                    const pa = totalAccounts > 0 ? (count / totalAccounts * 100).toFixed(1) : '0'
                                    return [
                                        `Value: $${this.formatUsd(val)}`,
                                        `${pv}% of total value`,
                                        `${this.formatNumber(count)} accounts (${pa}%)`,
                                    ]
                                },
                            }
                        },
                    },
                },
            })
        },

        renderHoldTimeChart() {
            if (this.holdTimeChart) this.holdTimeChart.destroy()
            if (!this.$refs.holdTimeCanvas || !this.holdTimeData?.histogram?.length) return

            const buckets = this.holdTimeData.histogram
            const labels = buckets.map(b => b.label)
            const colors = window.singleColor || ['#8b5cf6']
            const tc = this.chartTextColor

            this.holdTimeChart = new Chart(this.$refs.holdTimeCanvas, {
                type: 'bar',
                data: {
                    labels,
                    datasets: [{
                        label: 'Holders',
                        data: buckets.map(b => b.count),
                        backgroundColor: colors[0] + '99',
                        borderColor: colors[0],
                        borderWidth: 1,
                        borderRadius: 3,
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                title: (ctx) => {
                                    const b = buckets[ctx[0].dataIndex]
                                    const to = b.to_seconds
                                        ? this.formatDuration(b.to_seconds)
                                        : 'forever'
                                    return `${this.formatDuration(b.from_seconds)} \u2013 ${to}`
                                },
                                label: (ctx) => `${this.formatNumber(ctx.parsed.y)} holders`
                            }
                        }
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            ticks: { color: tc, font: { size: 11 } }
                        },
                        y: {
                            grid: { color: this.chartGridColor },
                            ticks: {
                                color: tc,
                                callback: (v) => this.formatNumber(v)
                            }
                        }
                    }
                }
            })
        },

        getLineChartOptions({ tooltipPrefix = '', tooltipSuffix = '', yFormat = null, yMax = undefined }) {
            const tc = this.chartTextColor
            return {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => `${ctx.dataset.label}: ${tooltipPrefix}${typeof yFormat === 'function' ? yFormat(ctx.parsed.y) : ctx.parsed.y}${tooltipSuffix}`
                        }
                    }
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: {
                            color: tc,
                            font: { size: 10 },
                            maxTicksLimit: 8,
                            autoSkip: true,
                        }
                    },
                    y: {
                        grid: { color: this.chartGridColor },
                        max: yMax,
                        ticks: {
                            color: tc,
                            callback: yFormat || undefined,
                        }
                    }
                }
            }
        },

        // === Formatters ===

        formatNumber(num) {
            if (num === null || num === undefined) return '0'
            if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M'
            if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K'
            return num.toLocaleString()
        },

        formatUsd(val) {
            if (!val) return '0'
            if (val >= 1e9) return (val / 1e9).toFixed(2) + 'B'
            if (val >= 1e6) return (val / 1e6).toFixed(2) + 'M'
            if (val >= 1e3) return (val / 1e3).toFixed(2) + 'K'
            return val.toFixed(2)
        },

        formatDuration(seconds) {
            if (!seconds || seconds <= 0) return '0s'
            if (seconds < 60) return Math.round(seconds) + 's'
            if (seconds < 3600) return Math.round(seconds / 60) + 'm'
            if (seconds < 86400) return (seconds / 3600).toFixed(1) + 'h'
            if (seconds < 86400 * 7) return Math.round(seconds / 86400) + 'd'
            if (seconds < 86400 * 30) return (seconds / (86400 * 7)).toFixed(1) + 'w'
            if (seconds < 86400 * 365) return (seconds / (86400 * 30)).toFixed(1) + 'mo'
            return (seconds / (86400 * 365)).toFixed(1) + 'y'
        },

        formatDateShort(ts) {
            if (!ts) return ''
            const d = new Date(ts)
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        },

        formatTime(ts) {
            if (!ts) return ''
            const d = new Date(ts)
            const now = new Date()
            const diff = (now - d) / 1000
            if (diff < 60) return 'just now'
            if (diff < 3600) return Math.floor(diff / 60) + 'm ago'
            if (diff < 86400) return Math.floor(diff / 3600) + 'h ago'
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        },

        truncAddr(addr) {
            if (!addr) return 'N/A'
            return addr.slice(0, 4) + '...' + addr.slice(-4)
        },
    }
}
</script>

<style scoped>
.holder-analytics-charts {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

/* Range selector */
.range-selector {
    display: flex;
    gap: 0.15rem;
    background: rgba(139, 92, 246, 0.08);
    border-radius: 8px;
    padding: 0.2rem;
    width: fit-content;
}

.range-btn {
    padding: 0.35rem 0.75rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    color: var(--compass-text-secondary, #7d8590);
    transition: all 0.2s ease;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.range-btn:hover:not(.active) {
    color: var(--compass-text-primary, #e6edf3);
    background: rgba(139, 92, 246, 0.1);
}

.range-btn.active {
    background: linear-gradient(135deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    color: #fff;
    font-weight: 700;
}

/* Chart sections */
.chart-section {
    padding: 1rem 1.25rem;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.section-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--compass-text-primary, #e6edf3);
    margin: 0;
}

.section-meta {
    font-size: 0.75rem;
    color: var(--compass-text-secondary, #7d8590);
}

.chart-sublabel {
    font-size: 0.7rem;
    color: var(--compass-text-secondary, #7d8590);
    margin-bottom: 0.4rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 500;
}

.chart-container {
    height: 220px;
    position: relative;
}

.treemap-container {
    height: 200px;
}

.chart-loading, .chart-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 120px;
    color: var(--compass-text-secondary, #7d8590);
    font-size: 0.8rem;
}

/* Legend */
.legend-inline {
    display: flex;
    gap: 0.75rem;
    font-size: 0.7rem;
    color: var(--compass-text-secondary, #7d8590);
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 4px;
}

.legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
}

/* Distribution table (horizontal bars) */
.distribution-table {
    margin-top: 0.5rem;
}

.dist-row {
    display: grid;
    grid-template-columns: 85px 1fr 70px 50px;
    gap: 0.5rem;
    align-items: center;
    padding: 0.35rem 0;
    font-size: 0.75rem;
    border-bottom: 1px solid var(--compass-border, rgba(139, 92, 246, 0.08));
}

.dist-row:last-child {
    border-bottom: none;
}

.dist-label {
    color: var(--compass-text-primary, #e6edf3);
    font-weight: 600;
    font-size: 0.72rem;
}

.dist-bar-wrap {
    height: 8px;
    background: var(--compass-bar-bg, rgba(139, 92, 246, 0.12));
    border-radius: 4px;
    overflow: hidden;
}

.dist-bar {
    height: 100%;
    background: linear-gradient(90deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    border-radius: 4px;
    display: block;
    min-width: 2px;
}

.dist-count {
    text-align: right;
    color: var(--compass-text-primary, #e6edf3);
    font-weight: 500;
}

.dist-pct {
    text-align: right;
    color: var(--compass-text-secondary, #7d8590);
}

/* Hold time percentiles */
.hold-time-stats {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.stat-pill {
    font-size: 0.7rem;
    color: var(--compass-text-secondary, #7d8590);
    background: rgba(139, 92, 246, 0.08);
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
}

.stat-pill strong {
    color: var(--compass-text-primary, #e6edf3);
}

.stat-pill-muted {
    font-style: italic;
    opacity: 0.8;
}

.percentile-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 0.5rem;
    margin-top: 0.75rem;
    border-top: 1px solid var(--compass-border, rgba(139, 92, 246, 0.15));
    padding-top: 0.75rem;
}

.percentile-item {
    text-align: center;
}

.perc-label {
    display: block;
    font-size: 0.6rem;
    color: var(--compass-text-secondary, #7d8590);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.2rem;
}

.perc-value {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--compass-text-primary, #e6edf3);
}

/* Whale movements */
.whale-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.range-label {
    font-size: 0.7rem;
    color: var(--compass-text-secondary, #7d8590);
}

.whale-select {
    background: var(--compass-input-bg, rgba(139, 92, 246, 0.1));
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    color: var(--compass-text-primary, #e6edf3);
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
}

.whale-table {
    font-size: 0.8rem;
}

.whale-table thead th {
    color: var(--compass-text-secondary, #7d8590);
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    border-bottom-color: var(--compass-border, rgba(139, 92, 246, 0.15));
}

.whale-table tbody td {
    color: var(--compass-text-primary, #e6edf3);
    border-bottom-color: var(--compass-border, rgba(139, 92, 246, 0.08));
    vertical-align: middle;
}

.td-time {
    white-space: nowrap;
}

.whale-wallet {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.wallet-addr {
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
    font-size: 0.75rem;
    color: var(--compass-purple, #8b5cf6);
    text-decoration: none;
}

.wallet-addr:hover {
    text-decoration: underline;
}

.whale-labels {
    display: flex;
    gap: 4px;
}

.direction-badge {
    font-size: 0.65rem;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}

.dir-buy {
    background: rgba(34, 197, 94, 0.15);
    color: var(--compass-success, #22c55e);
}

.dir-sell {
    background: rgba(239, 68, 68, 0.15);
    color: var(--compass-danger, #ef4444);
}

.pair-cell {
    white-space: nowrap;
    font-size: 0.75rem;
}

.pair-arrow {
    color: var(--compass-text-secondary, #7d8590);
    margin: 0 3px;
}

.exchange-badge {
    font-size: 0.65rem;
    padding: 2px 6px;
    border-radius: 3px;
    background: rgba(139, 92, 246, 0.12);
    color: var(--compass-text-secondary, #7d8590);
    font-weight: 500;
}

/* Label badges */
.label-badge {
    font-size: 0.55rem;
    padding: 1px 5px;
    border-radius: 3px;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.02em;
}

.label-cex { background: rgba(239, 68, 68, 0.2); color: #ef4444; }
.label-kol { background: rgba(139, 92, 246, 0.2); color: #8b5cf6; }
.label-treasury { background: rgba(16, 185, 129, 0.2); color: #10b981; }
.label-market-maker { background: rgba(59, 130, 246, 0.2); color: #3b82f6; }
.label-mm { background: rgba(59, 130, 246, 0.2); color: #3b82f6; }
.label-exchange { background: rgba(239, 68, 68, 0.2); color: #ef4444; }
.label-exchange-wallet { background: rgba(239, 68, 68, 0.2); color: #ef4444; }
.label-whale { background: rgba(234, 179, 8, 0.2); color: #eab308; }

/* === Light mode === */
body:not(.dark-mode) .range-selector {
    background: rgba(139, 92, 246, 0.06);
}

body:not(.dark-mode) .range-btn {
    color: #6b7280;
}

body:not(.dark-mode) .range-btn:hover:not(.active) {
    color: #374151;
    background: rgba(139, 92, 246, 0.08);
}

body:not(.dark-mode) .range-btn.active {
    color: #ffffff;
}

body:not(.dark-mode) .section-title {
    color: #1f2937;
}

body:not(.dark-mode) .section-meta,
body:not(.dark-mode) .chart-sublabel {
    color: #6b7280;
}

body:not(.dark-mode) .stat-pill {
    background: rgba(139, 92, 246, 0.06);
    color: #6b7280;
}

body:not(.dark-mode) .stat-pill strong {
    color: #1f2937;
}

body:not(.dark-mode) .dist-label {
    color: #374151;
}

body:not(.dark-mode) .dist-count {
    color: #1f2937;
}

body:not(.dark-mode) .dist-pct {
    color: #6b7280;
}

body:not(.dark-mode) .dist-bar-wrap {
    background: rgba(139, 92, 246, 0.08);
}

body:not(.dark-mode) .dist-row {
    border-bottom-color: rgba(0, 0, 0, 0.06);
}

body:not(.dark-mode) .perc-label {
    color: #6b7280;
}

body:not(.dark-mode) .perc-value {
    color: #1f2937;
}

body:not(.dark-mode) .whale-select {
    background: #f3f4f6;
    border-color: #d1d5db;
    color: #1f2937;
}

body:not(.dark-mode) .whale-table thead th {
    color: #6b7280;
    border-bottom-color: #e5e7eb;
}

body:not(.dark-mode) .whale-table tbody td {
    color: #1f2937;
    border-bottom-color: #f3f4f6;
}

body:not(.dark-mode) .exchange-badge {
    background: rgba(139, 92, 246, 0.08);
    color: #6b7280;
}

body:not(.dark-mode) .range-label {
    color: #6b7280;
}

body:not(.dark-mode) .chart-loading,
body:not(.dark-mode) .chart-empty {
    color: #6b7280;
}

body:not(.dark-mode) .legend-inline {
    color: #6b7280;
}

body:not(.dark-mode) .pair-arrow {
    color: #9ca3af;
}

body:not(.dark-mode) .percentile-grid {
    border-top-color: #e5e7eb;
}

/* === Dark mode explicit fixes === */
.dark-mode .whale-select {
    background: rgba(30, 30, 40, 0.8);
    border-color: rgba(139, 92, 246, 0.3);
    color: #e6edf3;
}

.dark-mode .whale-select option {
    background: #1a1a2e;
    color: #e6edf3;
}

.dark-mode .whale-table tbody td {
    color: #d1d5db;
}

.dark-mode .dist-label {
    color: #d1d5db;
}

.dark-mode .dist-count {
    color: #e6edf3;
}

/* Responsive */
@media (max-width: 768px) {
    .chart-container {
        height: 180px;
    }

    .treemap-container {
        height: 160px;
    }

    .percentile-grid {
        grid-template-columns: repeat(3, 1fr);
    }

    .dist-row {
        grid-template-columns: 70px 1fr 60px 45px;
        font-size: 0.7rem;
    }

    .hold-time-stats {
        flex-wrap: wrap;
    }

    .section-header {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>
